import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, getDocs } from "firebase/firestore";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

const SearchResults = ({ searchQuery }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allData, setAllData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  const navigate = useNavigate();
  const dataExpirationTime = 30 * 60 * 1000; // 30 minutes

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const collections = [
          "Allergies", "Analgesics", "AntiInfectives", "Antimalaria",
          "Cardio", "Consumables", "Contraceptives", "Cough",
          "Creams", "Diabetics", "Eye", "Gastro",
          "Haematinics", "Multivitamins", "Paediatrics", "Supplements",
        ];

        const allData = [];
        for (const collectionName of collections) {
          const q = query(collection(getFirestore(), collectionName));
          const querySnapshot = await getDocs(q);
          const data = querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            category: collectionName,
          }));
          allData.push(...data);
        }

        localStorage.setItem("productData", JSON.stringify(allData));
        localStorage.setItem("dataTimestamp", Date.now());
        setAllData(allData);
        setDataLoading(false);
      } catch (err) {
        setError("Failed to load data. Please try again later.");
        setDataLoading(false);
      }
    };

    const storedData = localStorage.getItem("productData");
    const storedTimestamp = localStorage.getItem("dataTimestamp");

    if (storedData && storedTimestamp) {
      const timeElapsed = Date.now() - storedTimestamp;
      if (timeElapsed < dataExpirationTime) {
        setAllData(JSON.parse(storedData));
        setDataLoading(false);
      } else {
        localStorage.removeItem("productData");
        localStorage.removeItem("dataTimestamp");
        fetchAllData();
      }
    } else {
      fetchAllData();
    }
  }, []);

  useEffect(() => {
    if (!dataLoading && searchQuery.trim()) {
      setHasSearched(true);
      setLoading(true);
      setSearchResults([]);

      setTimeout(() => {
        const lowercasedQuery = searchQuery.toLowerCase();
        const results = allData.filter((item) => {
          if (item && item.ID && item.description) {
            return (
              item.ID.toLowerCase().includes(lowercasedQuery) ||
              item.description.toLowerCase().includes(lowercasedQuery)
            );
          }
          return false;
        });

        setSearchResults(results);
        setLoading(false);
      }, 500);
    } else {
      setHasSearched(false);
      setSearchResults([]);
    }
  }, [searchQuery, allData, dataLoading]);

  const isOutOfStock = (product) => ["NO", "No", "N", "n", "no"].includes(product.available);

  return (
    <div className="mt-4 bg-white rounded-xl shadow-lg overflow-hidden max-h-96 overflow-y-auto border border-gray-100">
      {error ? (
        <div className="p-4 bg-red-50 border-b border-red-100">
          <p className="text-red-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        </div>
      ) : dataLoading ? (
        <div className="flex justify-center items-center p-6">
          <ClipLoader size={35} color="#10B981" />
          <span className="ml-3 text-gray-600 font-medium">Loading products...</span>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center p-6">
          <ClipLoader size={35} color="#10B981" />
          <span className="ml-3 text-gray-600 font-medium">Searching...</span>
        </div>
      ) : hasSearched && searchResults.length === 0 ? (
        <div className="p-6 text-center bg-yellow-50">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-yellow-800 mb-1">No products found</h3>
          <p className="text-sm text-yellow-600">Try searching for another drug brand</p>
        </div>
      ) : (
        searchResults.map((product) => (
          <div
            key={product.id}
            onClick={() =>
              !isOutOfStock(product) &&
              navigate(`/Productdetails/${product.id}`, { state: { product } })
            }
            className={`p-4 transition-all duration-200 border-b border-gray-100 last:border-b-0 ${
              isOutOfStock(product) 
                ? 'bg-gray-50 cursor-not-allowed' 
                : 'hover:bg-emerald-50 cursor-pointer hover:shadow-sm'
            }`}
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={product.image || "https://via.placeholder.com/56"}
                    alt={product.ID}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-gray-900 truncate">{product.ID}</h4>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm font-bold text-emerald-700">₦{product.Price}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                    {product.category}
                  </span>
                </div>
                {isOutOfStock(product) && (
                  <div className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default SearchResults;